/* Copyright (c) 2022 Xperi. All Rights Reserved. */

/**
 * This file implements all the common functions across the features,
 * which are exposed from the data/domain layer to other layers.
 * Any modifications to the exposed functions can be made here
 */

import { ActionKernelType, ActionType } from 'client-code/actions';
import { BodyInfo } from 'client-code/body-info-service';
import { StationPolicy } from 'client-code/channels-service';
import { SeasonsData } from 'client-code/episode-guide';
import { Feature, IpOnlyResponse } from 'client-code/fe-fav-service';
import { MyShowsFilterList } from 'client-code/myshows';
import { OnePassValues } from 'client-code/onepass';
import { ScreenType } from 'client-code/previews';
import {
    MyShowsUserSettings,
    MyShowsUserSettingsValue,
    OnePassAndRecordingsUserSettings,
    OnePassAndRecordingsUserSettingsValues,
} from 'client-code/user-settings';
import {
    DrmType,
    TransportEncodingType,
    VideoProviderConfiguration,
} from 'client-code/video-provider-configurations';
import { SubEvent } from 'sub-events';

import {
    DEFAULT_RETRY_OPTIONS,
    IDLE_GRID_GUIDE_DURATION,
    MILLISECONDS_IN_A_SECOND,
    TIVO_SERVICES,
} from '../../config';
import { AppUtils } from '../AppUtils';
import { Branding } from '../branding/Branding';
import { DeviceFeatures } from '../device';
import { DeviceModel } from '../device/DeviceModel';
import { DimensionScaler } from '../device/DimensionScaler';
import { BrandingUiInfoKeys, Channel, GuideCell, PCSettings, Session } from '../entities';
import { PCState } from '../enums';
import {
    EmptyResponseError,
    ErrorTypes,
    OfferActionDetailsError,
    OfferActionError,
    PartialResponseError,
    TypedChannelsNotFoundError,
} from '../error';
import { ParentalControlServiceImpl } from '../impls';
import {
    ActionDetailsService,
    ActionsService,
    AiringOfferService,
    BodyInfoService,
    BookmarkService,
    BrandingUIBundleService,
    ChannelSettingsService,
    ChannelsService,
    EpisodeGuideService,
    FeFavFilterService,
    GridGuideService,
    InHomeLocationService,
    InternalRatingsService,
    MyShowsService,
    MyWanIpService,
    OnePassService,
    ParentalControlService,
    PreviewsService,
    RecordingsService,
    RecordingsStatusService,
    Services,
    SessionManagerService,
    SlsServiceEndpointsSearchService,
    StationPolicyService,
    UpcomingOffersService,
    UserSettingsService,
    VodSocuLastViewedPositionService,
    VPConfigurationsService,
} from '../infrastructure';
import { IRecordingPreviewParams, IServices } from '../interfaces';
import { Locale, LocaleKeys } from '../locale';
import { Log } from '../logging';
import { PlaybackContentType } from '../playback/enums';
import { RecoveryMechanism } from '../recoverymechanism/RecoveryMechanism';
import { RetryMechanism } from '../retrymechanism/RetryMechanism';
import { RetryOptions } from '../retrymechanism/RetryOptions';
import { TiVoNetworkRequestModel } from '../tivo';
import {
    TActionDetailsList,
    TActionDetailsParams,
    TActionDetailsParamsContentId,
    TActionList,
    TActionsContentParams,
    TActionsParams,
    TActionsRecordingParams,
    TActionsUnifiedCollectionParams,
    TActionsVodOfferParams,
    TAiringOffer,
    TChannels,
    TContentPreview,
    TCreateSessionParams,
    TExtendedRatingLimits,
    TGridGuideRow,
    TGuideCell,
    TInHomeLocation,
    TInternalRating,
    TIpAddress,
    TMetadata,
    TMyShowsItemList,
    TMyShowsItemListParams,
    TOfferPreview,
    TOfferPreviewCustomData,
    TOneLineGuideRow,
    TOnePassList,
    TOnePassListParams,
    TOnePassOptions,
    TOnePassValuesWithDisplayOptions,
    TPCRating,
    TPCRatingsData,
    TPCServiceParams,
    TPlayNextContentActionsParams,
    TPlayNextInfo,
    TPlayNextParams,
    TPlayNextRecordingActionsParams,
    TRecordingOptions,
    TRecordingValues,
    TRecordingValuesWithDisplayOptions,
    TSeasonsParams,
    TServicesParams,
    TSession,
    TSessionState,
    TStorageInfo,
    TUpcomingOffersList,
    TUpcomingOffersParams,
    TVodSocuLastViewedPositionParams,
    TVuLoggerProperties,
} from '../types';
import { ChannelsType } from '../types/TChannelsType';
import { BrandingUiUtility, DateTime, UniqueIdentifier } from '../utils';
import { CcbSwitch } from '../utils/ccb/CcbSwitch';
import { CcbSwitchName } from '../utils/ccb/enums/CcbSwitchName';
import { IPlayable } from '../videoplayer/interfaces';

export class ServicesClientImpl implements IServices {
    private static TAG = 'ServicesClientImpl';

    public static servicesBaseUrls: { [key: string]: string } = {};

    private static parentalSettings: Feature | null = null;

    public static inHomeLocationDataUpdateEvent: SubEvent<boolean> = new SubEvent();

    // eslint-disable-next-line class-methods-use-this
    protected getChannelsServiceParams() {
        return {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ChannelsService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            channelLogoBaseUrl:
                ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ChannelLogoImageHost],
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        };
    }

    private isSlsServiceEndpointResIncomplete = (slsEndpoints: { [key: string]: string }) => {
        const services = Object.keys(slsEndpoints);
        const postSigninServices = Object.values(TIVO_SERVICES);
        for (let i = 0; i < postSigninServices.length; i += 1) {
            if (!services.includes(postSigninServices[i])) {
                return true;
            }
        }
        return false;
    };

    private validateSlsServiceResponse = (slsResponse: { [key: string]: string }) => {
        if (!Object.keys(slsResponse).length) {
            throw new EmptyResponseError(
                ErrorTypes.SLS_SE_EMPTY_RESPONSE_C806,
                'Received empty response from SlsServiceEndpointGet',
                this.formatErrorDescription(
                    Locale.getString(LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_DESCRIPTION),
                ),
            );
        }

        if (this.isSlsServiceEndpointResIncomplete(slsResponse)) {
            throw new PartialResponseError(
                ErrorTypes.SLS_SE_PARTIAL_RESPONSE_C805,
                'Received partial response from SlsServiceEndpointGet',
                this.formatErrorDescription(
                    Locale.getString(LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_DESCRIPTION),
                ),
            );
        }
    };

    // TODO: move this method some better place.
    protected formatErrorDescription = (description: string) => {
        let formattedDescription = description;
        formattedDescription = formattedDescription.replace(
            'PHONE_CUSTOMER_SUPPORT_NUMBER',
            this.getBrandingUiInfoForKey(BrandingUiInfoKeys.PHONE_CUSTOMER_SUPPORT_NUMBER),
        );
        formattedDescription = formattedDescription.replace(
            'CUSTOMER_SUPPORT_NAME',
            this.getBrandingUiInfoForKey(BrandingUiInfoKeys.CUSTOMER_SUPPORT_NAME),
        );
        return formattedDescription;
    };

    private formatSocuErrorDescription = (description: string) => {
        const replaceableValues = {
            PHONE_CUSTOMER_SUPPORT_NUMBER: this.getBrandingUiInfoForKey(
                BrandingUiInfoKeys.PHONE_CUSTOMER_SUPPORT_NUMBER,
            ),
            CUSTOMER_SUPPORT_NAME: this.getBrandingUiInfoForKey(
                BrandingUiInfoKeys.CUSTOMER_SUPPORT_NAME,
            ),

            URL_ERROR: this.getBrandingUiInfoForKey(BrandingUiInfoKeys.URL_ERROR),
        };
        return BrandingUiUtility.formatDescription(description, replaceableValues);
    };

    private _fetchDeviceFeatures = () => {
        return Services.getInstanceOf(FeFavFilterService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.FeFavFilterService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).fetchDeviceFeaturesFromService();
    };

    private _fetchSlsServiceEndpoints = (): Promise<void> => {
        return new Promise((resolve, reject) => {
            new SlsServiceEndpointsSearchService(DeviceModel.getSlsBaseUrl())
                .fetchSlsServiceEndpoints()
                .then((response) => {
                    this.validateSlsServiceResponse(response);
                    ServicesClientImpl.servicesBaseUrls = response;
                    resolve();
                })
                .catch((error) => {
                    Log.e(ServicesClientImpl.TAG, 'Failed to get SLS service endpoints.');
                    Log.d(ServicesClientImpl.TAG, error);
                    reject(error);
                });
        });
    };

    private _validateOfferActionServiceResponse = (data: TActionList) => {
        const { actionList } = data;
        if (!actionList || !Object.keys(actionList).length) {
            throw new EmptyResponseError(
                ErrorTypes.OFFER_ACTION_EMPTY_RESPONSE_ERROR_V203,
                'empty response from v1/offer/action api',
                this.formatSocuErrorDescription(
                    Locale.getString(LocaleKeys.SOCU_ERROR_OVERLAY_DESCRIPTION),
                ),
                Locale.getString(LocaleKeys.SOCU_ERROR_OVERLAY_TITLE),
            );
        }
        const watchFromProviderAction = actionList.actions.find((action) => {
            return (
                action.actionType === ActionType.WatchFromProviderAction &&
                action.kernel?.actionKernelType === ActionKernelType.WatchFromSocuActionKernel
            );
        });
        if (!watchFromProviderAction) {
            throw new PartialResponseError(
                ErrorTypes.SOCU_PROVIDER_NOT_FOUND_V290,
                'socu provider not found in action api response',
                this.formatSocuErrorDescription(
                    Locale.getString(LocaleKeys.SOCU_ERROR_OVERLAY_DESCRIPTION),
                ),
                Locale.getString(LocaleKeys.SOCU_ERROR_OVERLAY_TITLE),
            );
        }
    };

    private _validateOfferActionsServiceV2Response = (data: TActionList) => {
        const { actionList } = data;
        if (!actionList || !Object.keys(actionList).length) {
            throw new EmptyResponseError(
                ErrorTypes.OFFER_ACTION_EMPTY_RESPONSE_ERROR_V203,
                'empty response from v2/offer/action api',
                this.formatSocuErrorDescription(
                    Locale.getString(LocaleKeys.SOCU_ERROR_OVERLAY_DESCRIPTION),
                ),
                Locale.getString(LocaleKeys.SOCU_ERROR_OVERLAY_TITLE),
            );
        }
    };

    private _validateActionDetailsServiceResponse = (data: TActionDetailsList) => {
        const { actionDetails } = data;
        if (!actionDetails || !Object.keys(actionDetails).length) {
            throw new EmptyResponseError(
                ErrorTypes.OTT_OFFER_ACTION_DETAILS_EMPTY_RESPONSE_ERROR_V306,
                'fetchOfferActionDetails empty response',
                this.formatSocuErrorDescription(
                    Locale.getString(LocaleKeys.SOCU_ERROR_OVERLAY_DESCRIPTION),
                ),
                Locale.getString(LocaleKeys.SOCU_ERROR_OVERLAY_TITLE),
            );
        }
        if (!actionDetails?.watchActionsList[0]?.kernel?.partnerId) {
            throw new PartialResponseError(
                ErrorTypes.SOCU_OFFER_PARTNER_ID_ERROR_V393,
                'The configured PartnerId for a specific offer is missing',
                this.formatSocuErrorDescription(
                    Locale.getString(LocaleKeys.SOCU_ERROR_OVERLAY_DESCRIPTION),
                ),
                Locale.getString(LocaleKeys.SOCU_ERROR_OVERLAY_TITLE),
            );
        }
    };

    public generateProviderImageUrl = (
        providerId: string,
        width: number,
        height: number,
    ): string => {
        const { width: scaledWidth, height: scaledHeight } =
            DimensionScaler.getInstance().getScaledDimensions(width, height);
        return `${ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ImageHost]}/images/${providerId}/partnerSourceLogo_${scaledWidth}x${scaledHeight}`;
    };

    public loadSlsServiceEndpoints = (): Promise<TServicesParams> => {
        const _getServiceParams = this._fetchSlsServiceEndpoints.bind(this);
        return new Promise((resolve, reject) => {
            RetryMechanism.callServiceWithRetry(
                _getServiceParams,
                [],
                <RetryOptions>DEFAULT_RETRY_OPTIONS,
            )
                .then((response) => {
                    resolve(<TServicesParams>response);
                })
                .catch((retryError) => {
                    reject(retryError);
                });
        });
    };

    public waitForSlsServiceEndpoints = (): Promise<TServicesParams> => {
        const _getServiceParams = this._fetchSlsServiceEndpoints.bind(this);
        return new Promise((resolve, reject) => {
            RecoveryMechanism.callServiceWithRecovery(_getServiceParams, [])
                .then((response) => {
                    resolve(<TServicesParams>response);
                })
                .catch((error) => {
                    if (
                        error instanceof PartialResponseError ||
                        error instanceof EmptyResponseError
                    ) {
                        resolve(this.waitForSlsServiceEndpoints());
                    } else reject(error);
                });
        });
    };

    public fetchVPConfigurations = (
        supportedDrmType: Array<DrmType>,
        supportedTransportEncodingType: Array<TransportEncodingType>,
    ): Promise<void> => {
        return Services.getInstanceOf(VPConfigurationsService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.VPConfigurationsService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).fetchVPConfigurations(supportedDrmType, supportedTransportEncodingType);
    };

    public loadVpConfigurations = (): Promise<boolean> => {
        const isMobiDrmEnabled = CcbSwitch.getInstance().isEnabledAndOverriden(
            CcbSwitchName.ENABLE_MOBI_DRM_REMOVE_IN_KST_7121,
        );

        const supportedDrmTypes: DrmType[] = isMobiDrmEnabled
            ? [DrmType.Widevine, DrmType.MobiWidevine]
            : [DrmType.Widevine];
        let supportedTransportEncodingTypes: TransportEncodingType[] = [
            // if we need to support multiple TransportStream, add below
            // and same need to be handled in VideoPlayerModelImpl for DRM
            TransportEncodingType.DashFmp4TransportStream,
        ];

        if (
            CcbSwitch.getInstance().isEnabledAndOverriden(
                CcbSwitchName.ENABLE_TRANSPORT_STREAM_FE_REMOVE_IN_KST_14322,
            )
        ) {
            supportedTransportEncodingTypes = [
                // if we need to support multiple TransportStream, add below
                // and same need to be handled in VideoPlayerModelImpl for DRM
                TransportEncodingType.DashFmp4TransportStream,
                TransportEncodingType.HlsFmp4TransportStream,
            ];
        }
        const fetchVPConfigurations = this.fetchVPConfigurations.bind(this);
        return new Promise((resolve, reject) => {
            RetryMechanism.callServiceWithRetry(
                fetchVPConfigurations,
                [supportedDrmTypes, supportedTransportEncodingTypes],
                <RetryOptions>DEFAULT_RETRY_OPTIONS,
            )
                .then(() => {
                    resolve(true);
                })
                .catch((error: any) => {
                    reject(error);
                });
        });
    };

    public fetchVPConfigurationsForVideoPlayer = (
        linearProviderPartnerId: string,
        forceNetworkCall?: boolean,
    ): Promise<Array<VideoProviderConfiguration>> => {
        return Services.getInstanceOf(VPConfigurationsService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.VPConfigurationsService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).fetchVPConfigurationsForVideoPlayer(linearProviderPartnerId, forceNetworkCall);
    };

    public fetchStationPolicies = (): Promise<Array<StationPolicy>> => {
        return Services.getInstanceOf(StationPolicyService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ChannelsService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).fetchStationPolicies();
    };

    public fetchAiringOffers = (
        stationId: string,
        networkCallId?: number,
        playerCurrentTime?: number,
    ): Promise<TAiringOffer> => {
        return Services.getInstanceOf(AiringOfferService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.AiringOffersService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).fetchAiringOffers(stationId, networkCallId, playerCurrentTime);
    };

    public fetchMyChannels = (offset?: number, limit?: number): Promise<TChannels> => {
        return Services.getInstanceOf(
            ChannelsService,
            this.getChannelsServiceParams(),
        ).fetchMyChannels(offset, limit);
    };

    public fetchAllChannels = (offset?: number, limit?: number): Promise<TChannels> => {
        return Services.getInstanceOf(
            ChannelsService,
            this.getChannelsServiceParams(),
        ).fetchAllChannels(offset, limit);
    };

    public loadChannelsInfo = (): Promise<boolean> => {
        const fetchAllChannels = this.fetchAllChannels.bind(this);
        return new Promise((resolve, reject) => {
            RetryMechanism.callServiceWithRetry(
                fetchAllChannels,
                [],
                <RetryOptions>DEFAULT_RETRY_OPTIONS,
            )
                .then(() => {
                    resolve(true);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    };

    public fetchChannels = async (
        channelType?: string,
        offset?: number,
        limit?: number,
    ): Promise<TChannels> => {
        const service = Services.getInstanceOf(ChannelsService, this.getChannelsServiceParams());
        let data: TChannels;
        switch (channelType) {
            case Locale.getString(LocaleKeys.GUIDE_CHANNEL_TYPES_MY_CHANNELS):
                data = await service.fetchMyChannels(offset, limit);
                break;
            case Locale.getString(LocaleKeys.GUIDE_CHANNEL_TYPES_FAVORITES):
                data = await service.fetchFavoriteChannels(offset, limit);
                break;
            case Locale.getString(LocaleKeys.GUIDE_CHANNEL_TYPES_ALL_CHANNELS):
            default:
                data = await service.fetchAllChannels(offset, limit);
                break;
        }

        if (!data.channels.length)
            throw new TypedChannelsNotFoundError(
                ErrorTypes.GUIDE_CHANNELS_SERVICE_EMPTY_RESPONSE_C1106,
                this.formatErrorDescription(
                    Locale.getString(LocaleKeys.GUIDE_TEMPORARY_GUIDE_ISSUE_OVERLAY_DESCRIPTION),
                ),
            );
        return data;
    };

    public fetchProgramMetadata = (
        stationId: string[],
        startTime: DateTime,
        endTime: DateTime,
    ): Promise<TGridGuideRow[]> => {
        return Services.getInstanceOf(GridGuideService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.GridGuideService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            imageHostBaseUrl: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ImageHost],
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).fetchProgramMetadata(stationId, startTime, endTime);
    };

    public fetchOneLineGuideRowMetadata = (
        channel: Channel,
        anchorTime: DateTime,
        count: number,
        offset: number,
        pastMode: boolean,
        networkCallId?: number,
    ): Promise<TOneLineGuideRow> => {
        const { stationId } = channel;
        if (!stationId) {
            return Promise.reject(new Error('Station ID is missing for the channel'));
        }

        return Services.getInstanceOf(GridGuideService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.GridGuideService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            imageHostBaseUrl: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ImageHost],
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).fetchOneLineGuideRowMetadata(
            stationId,
            anchorTime,
            count,
            offset,
            pastMode,
            networkCallId,
        );
    };

    /**
     * This method will set a certain channel favorite status based on 'isFavorite' parameter
     * and in case the call was made succesfuly, a 'true' value will be returned.
     * @param channel (Channel) - The channel that will have the settings changed
     * @param isFavorite (boolean) - The new status for channel
     * @returns true if call returns 204, false otherwise
     */
    public setChannelSettings = (channel: Channel, isFavorite: boolean): Promise<boolean> => {
        return Services.getInstanceOf(ChannelSettingsService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ChannelSettings],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).setChannelSettings(channel, isFavorite);
    };

    private static _createDummyGuideRowData = (
        channel: Channel,
        currentTimestamp: DateTime,
        nextTimestamp: DateTime,
    ) => {
        const { stationId, affiliate, callSign, videoResolution } = channel;
        const guideRow = {
            stationId,
            guideCells: [
                {
                    title: `${Locale.getString(LocaleKeys.GUIDE_SELECT_TO_WATCH_TEXT)} ${
                        affiliate?.split(' ')[0] || callSign
                    } ${videoResolution?.toUpperCase()}`,
                    startTime: currentTimestamp,
                    duration:
                        (nextTimestamp.getTime() - currentTimestamp.getTime()) /
                        MILLISECONDS_IN_A_SECOND,
                },
            ],
        };
        return guideRow;
    };

    private _createDummyGuideCell = (
        startTime: DateTime,
        endTime: DateTime,
        title: string = Locale.getString(LocaleKeys.TITLE_NOT_AVAILABLE),
    ): GuideCell => {
        const duration: number =
            (endTime?.getTime() - startTime?.getTime()) / MILLISECONDS_IN_A_SECOND;
        return {
            collectionId: '',
            contentId: '',
            duration,
            offerId: '',
            posterImageUrl: '',
            startTime,
            title,
        };
    };

    private _validateGuideRows = (
        guideRows: TGridGuideRow[],
        stationIds: string[],
        startTime: DateTime,
        endTime: DateTime,
    ): TGridGuideRow[] => {
        const mGuideRows = guideRows.map((guideRow, index) => {
            if (!guideRow || guideRow.guideCells.length === 0) {
                return <TGridGuideRow>{
                    stationId: stationIds[index],
                    guideCells: [this._createDummyGuideCell(startTime, endTime)],
                };
            }
            const mGuideCells: TGuideCell[] = [];
            guideRow.guideCells.forEach((guideCell, idx): void => {
                const nextGuideCell = guideRow.guideCells[idx + 1];
                const currentGuideCellEndTime: DateTime = guideCell.startTime.addSeconds(
                    guideCell.duration,
                );

                if (idx === 0 && guideCell.startTime.getTime() > startTime.getTime()) {
                    mGuideCells.push(this._createDummyGuideCell(startTime, guideCell.startTime));
                    mGuideCells.push(guideCell);
                    if (
                        guideRow.guideCells.length === idx + 1 &&
                        currentGuideCellEndTime.getTime() < endTime.getTime()
                    ) {
                        mGuideCells.push(
                            this._createDummyGuideCell(currentGuideCellEndTime, endTime),
                        );
                    }
                } else if (
                    nextGuideCell &&
                    currentGuideCellEndTime.getTime() < nextGuideCell.startTime.getTime()
                ) {
                    mGuideCells.push(guideCell);
                    mGuideCells.push(
                        this._createDummyGuideCell(
                            currentGuideCellEndTime,
                            nextGuideCell.startTime,
                        ),
                    );
                } else if (
                    guideRow.guideCells.length === idx + 1 &&
                    currentGuideCellEndTime.getTime() < endTime.getTime()
                ) {
                    mGuideCells.push(guideCell);
                    mGuideCells.push(this._createDummyGuideCell(currentGuideCellEndTime, endTime));
                } else {
                    mGuideCells.push(guideCell);
                }
            });
            return {
                stationId: stationIds[index],
                guideCells: mGuideCells,
            };
        });

        return mGuideRows;
    };

    public fetchGuideRowsData = (
        channels: Channel[],
        startTime: DateTime,
        endTime: DateTime,
        guideStartTime?: DateTime,
    ): Promise<TGridGuideRow[]> => {
        const stationIds: string[] = channels.map(({ stationId = '' }) => {
            return stationId;
        });
        const _guideStartTime = guideStartTime || new DateTime();

        return new Promise((resolve, reject) => {
            this.fetchProgramMetadata(stationIds, startTime, endTime)
                .then((guideRows) => {
                    const mGuideRows = this._validateGuideRows(
                        guideRows,
                        stationIds,
                        startTime,
                        endTime,
                    );
                    resolve(mGuideRows);
                })
                .catch((error) => {
                    Log.e(ServicesClientImpl.TAG, error);

                    const dummyGuideRows = channels.map((channel) => {
                        return ServicesClientImpl._createDummyGuideRowData(
                            channel,
                            _guideStartTime,
                            _guideStartTime.addMinutes(IDLE_GRID_GUIDE_DURATION),
                        );
                    });
                    const modifiedError = { ...error, dummyGuideRows };
                    reject(modifiedError);
                });
        });
    };

    public fetchOfferPreview = (
        offerId: string,
        screenType: ScreenType,
        networkCallId?: number,
    ): Promise<TOfferPreview> => {
        return Services.getInstanceOf(PreviewsService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.PreviewsService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            imageHostBaseUrl: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ImageHost],
            timeZoneName: DeviceModel.getTVProperties().getDefaultTimeZone(),
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).fetchOfferPreview(offerId, screenType, networkCallId);
    };

    public fetchVodOfferPreview = (
        vodOfferId: string,
        screenType: ScreenType,
        networkCallId?: number,
    ): Promise<TOfferPreview> => {
        return Services.getInstanceOf(PreviewsService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.PreviewsService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            timeZoneName: DeviceModel.getTVProperties().getDefaultTimeZone(),
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).fetchVodOfferPreview(vodOfferId, screenType, networkCallId);
    };

    public getStandardBannerVodOfferPreviewByOfferId = (
        offerId: string,
    ): Promise<TOfferPreview> => {
        return this.fetchVodOfferPreview(
            offerId,
            ScreenType.StandardBanner,
            UniqueIdentifier.getNetworkCallId(),
        );
    };

    public getFullBannerVodOfferPreviewByOfferId = (offerId: string): Promise<TOfferPreview> => {
        return this.fetchVodOfferPreview(
            offerId,
            ScreenType.FullBanner,
            UniqueIdentifier.getNetworkCallId(),
        );
    };

    public getCompleteVodOfferPreviewData = async (
        offerId: string,
        standardBannerResponse: TOfferPreviewCustomData,
        screenType: ScreenType,
    ): Promise<TOfferPreviewCustomData> => {
        if (screenType === ScreenType.StandardBanner) {
            return (await this.getStandardBannerVodOfferPreviewByOfferId(offerId)).offerPreview;
        }
        const fullBannerResponse = await this.getFullBannerVodOfferPreviewByOfferId(offerId);
        return AppUtils.mergeObjects<TOfferPreviewCustomData>(
            standardBannerResponse,
            fullBannerResponse.offerPreview,
        );
    };

    public getOfferActions = (actionsParams: TActionsParams): Promise<TActionList> => {
        return new Promise((resolve, reject) => {
            const offerActionsService = Services.getInstanceOf(ActionsService, {
                accessToken: DeviceModel.getAccessToken(),
                basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ActionsService],
                bodyId: DeviceModel.getTiVoSettings().getBodyId(),
                timeZoneName: DeviceModel.getTVProperties().getDefaultTimeZone(),
                whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
            });
            offerActionsService
                .getOfferActions(actionsParams)
                .then((data) => {
                    this._validateOfferActionServiceResponse(data);
                    resolve(data);
                })
                .catch((error: any) => {
                    let offerActionError = error;
                    if (
                        !(
                            error instanceof EmptyResponseError ||
                            error instanceof PartialResponseError
                        )
                    ) {
                        const offerActionServiceError = error?.response?.data;
                        offerActionError = new OfferActionError(
                            offerActionServiceError?.message,
                            offerActionServiceError?.code,
                            this.formatSocuErrorDescription(
                                Locale.getString(LocaleKeys.SOCU_ERROR_OVERLAY_DESCRIPTION),
                            ),
                        );
                    }
                    reject(offerActionError);
                });
        });
    };

    public createSession = (
        sessionParams: TCreateSessionParams,
        sessionNetworkCallId?: number,
    ): Promise<TSession> => {
        return Services.getInstanceOf(SessionManagerService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.SessionManagerService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).createSession(sessionParams, sessionNetworkCallId);
    };

    public sessionKeepAlive = (
        session: Session,
        sessionNetworkCallId?: number,
    ): Promise<TSessionState> => {
        return Services.getInstanceOf(SessionManagerService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.SessionManagerService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).sessionKeepAlive(session, sessionNetworkCallId);
    };

    public sessionDelete = (
        session: Session,
        sessionNetworkCallId?: number,
    ): Promise<TSessionState> => {
        return Services.getInstanceOf(SessionManagerService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.SessionManagerService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).sessionDelete(session, sessionNetworkCallId);
    };

    public updateFavoriteChannel = (channel: Channel, isFavorite: boolean): Promise<void> => {
        return Services.getInstanceOf(
            ChannelsService,
            this.getChannelsServiceParams(),
        ).updateFavoriteChannel(channel, isFavorite);
    };

    public getNextChannel = (
        channelNumber: string,
        channelsType: string,
        count?: number,
    ): Promise<Channel> => {
        return Services.getInstanceOf(
            ChannelsService,
            this.getChannelsServiceParams(),
        ).getNextChannel(channelNumber, channelsType, count);
    };

    public getPreviousChannel = (
        channelNumber: string,
        channelsType: string,
        count?: number,
    ): Promise<Channel> => {
        return Services.getInstanceOf(
            ChannelsService,
            this.getChannelsServiceParams(),
        ).getPreviousChannel(channelNumber, channelsType, count);
    };

    public getChannelFromChannelNumber = (channelNumber: string): Promise<Channel> => {
        return Services.getInstanceOf(
            ChannelsService,
            this.getChannelsServiceParams(),
        ).tuneToChannel(channelNumber);
    };

    public fetchAndStoreInternalRatingsData = (): Promise<void> => {
        return Services.getInstanceOf(InternalRatingsService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.InternalRatingsService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            language: DeviceModel.getTVProperties().getLang(),
            msoPartnerId: DeviceModel.getTiVoSettings().getMsoPartnerId(),
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).fetchAndStoreInternalRatingsData();
    };

    public recoverInternalRatingsInBackground = () => {
        const fetchAndStoreInternalRatingsData = this.fetchAndStoreInternalRatingsData.bind(this);
        RecoveryMechanism.callServiceWithRecovery(fetchAndStoreInternalRatingsData, []);
    };

    public loadInternalRatings = (): Promise<boolean> => {
        const fetchAndStoreInternalRatingsData = this.fetchAndStoreInternalRatingsData.bind(this);
        return new Promise((resolve, reject) => {
            RetryMechanism.callServiceWithRetry(
                fetchAndStoreInternalRatingsData,
                [],
                <RetryOptions>DEFAULT_RETRY_OPTIONS,
            )
                .then(() => {
                    resolve(true);
                })
                .catch((error: any) => {
                    reject(error);
                });
        });
    };

    public getLastPlayedOrFallbackToFirstByChannelNumber = async (
        channelNumber: string | null,
    ): Promise<IPlayable> => {
        let channel: Channel;
        try {
            if (channelNumber === null) throw new Error('Last tuned channel not found.');
            channel = await new ServicesClientImpl().getChannelFromChannelNumber(channelNumber);
        } catch (error) {
            const channels: TChannels = await this.fetchMyChannels(0, 1);
            channel = channels?.channels[0];
        }
        return { channel, playbackContentType: PlaybackContentType.LIVE };
    };

    public getInternalRatingName = (ratingKey: string): string => {
        return Services.getInstanceOf(InternalRatingsService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.InternalRatingsService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            language: DeviceModel.getTVProperties().getLang(),
            msoPartnerId: DeviceModel.getTiVoSettings().getMsoPartnerId(),
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).getInternalRatingName(ratingKey);
    };

    // eslint-disable-next-line class-methods-use-this
    public getInternalRatingsData(): any {
        return Services.getInstanceOf(InternalRatingsService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.InternalRatingsService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            language: DeviceModel.getTVProperties().getLang(),
            msoPartnerId: DeviceModel.getTiVoSettings().getMsoPartnerId(),
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).getInternalRatingsData();
    }

    public fetchBodyInfo = (): BodyInfo => {
        return Services.getInstanceOf(BodyInfoService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.BodyInfoService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
        }).fetchBodyInfo();
    };

    public fetchAndStoreBodyInfoData = (): Promise<BodyInfo> => {
        return Services.getInstanceOf(BodyInfoService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.BodyInfoService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).fetchAndStoreBodyInfoData();
    };

    public loadBodyInfo = (): Promise<boolean> => {
        const fetchAndStoreBodyInfoData = this.fetchAndStoreBodyInfoData.bind(this);
        return new Promise((resolve, reject) => {
            RetryMechanism.callServiceWithRetry(
                fetchAndStoreBodyInfoData,
                [],
                <RetryOptions>DEFAULT_RETRY_OPTIONS,
            )
                .then(() => {
                    resolve(true);
                })
                .catch((error: any) => {
                    reject(error);
                });
        });
    };

    public waitForBodyInfo = (): Promise<boolean> => {
        const fetchAndStoreBodyInfoData = this.fetchAndStoreBodyInfoData.bind(this);
        return new Promise((resolve, reject) => {
            RecoveryMechanism.callServiceWithRecovery(fetchAndStoreBodyInfoData, [])
                .then(() => {
                    resolve(true);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    };

    public fetchNextChannelsFromChannelNumber = (
        channelNumber: string,
        channelsType: string,
        limit: number,
    ): Promise<TChannels> => {
        let mChannelsType: ChannelsType;

        switch (channelsType) {
            case Locale.getString(LocaleKeys.GUIDE_CHANNEL_TYPES_ALL_CHANNELS):
                mChannelsType = ChannelsType.ALL;
                break;
            case Locale.getString(LocaleKeys.GUIDE_CHANNEL_TYPES_FAVORITES):
                mChannelsType = ChannelsType.FAVORITE;
                break;
            case Locale.getString(LocaleKeys.GUIDE_CHANNEL_TYPES_MY_CHANNELS):
            default:
                mChannelsType = ChannelsType.SUBSCRIBED;
        }
        return Services.getInstanceOf(
            ChannelsService,
            this.getChannelsServiceParams(),
        ).fetchNextChannelsFromChannelNumber(channelNumber, mChannelsType, limit);
    };

    public getInHomeLocation = (): TInHomeLocation => {
        return Services.getInstanceOf(InHomeLocationService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.InHomeLocationService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).getInHomeLocation();
    };

    public fetchAndStoreInHomeLocation = (): Promise<TInHomeLocation> => {
        return Services.getInstanceOf(InHomeLocationService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.InHomeLocationService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).fetchAndStoreInHomeLocation();
    };

    public loadInHomeLocationInfo = (): Promise<boolean> => {
        const fetchAndStoreInHomeLocation = this.fetchAndStoreInHomeLocation.bind(this);
        return new Promise((resolve, reject) => {
            RetryMechanism.callServiceWithRetry(
                fetchAndStoreInHomeLocation,
                [],
                <RetryOptions>DEFAULT_RETRY_OPTIONS,
            )
                .then(() => {
                    resolve(true);
                    ServicesClientImpl.inHomeLocationDataUpdateEvent.emit(true);
                })
                .catch((error: any) => {
                    reject(error);
                });
        });
    };

    public getBrandingUIBundleInfo = (): Promise<void> => {
        return Services.getInstanceOf(BrandingUIBundleService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.BrandingUIBundleService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            slsDomain: DeviceModel.getTiVoSettings().getSlsDomain(),
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).getBrandingUiBundleInfo();
    };

    public loadBrandingUiBundleInfo = (): Promise<boolean> => {
        const getBrandingUIBundleInfo = this.getBrandingUIBundleInfo.bind(this);
        return new Promise((resolve, reject) => {
            RetryMechanism.callServiceWithRetry(
                getBrandingUIBundleInfo,
                [],
                <RetryOptions>DEFAULT_RETRY_OPTIONS,
            )
                .then(() => {
                    resolve(true);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    };

    public waitForBrandingUiBundleInfo = (): Promise<boolean> => {
        const getBrandingUIBundleInfo = this.getBrandingUIBundleInfo.bind(this);
        return new Promise((resolve) => {
            RecoveryMechanism.callServiceWithRecovery(getBrandingUIBundleInfo, []);
            resolve(true);
        });
    };

    public getDeviceFeatureByName = (name: string, defaultValue: Feature): Feature => {
        try {
            return Services.getInstanceOf(FeFavFilterService, {
                accessToken: DeviceModel.getAccessToken(),
                basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.FeFavFilterService],
                bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            }).getDeviceFeatureByName(name);
        } catch (error) {
            Log.e(ServicesClientImpl.TAG, 'getDeviceFeatureByName', error);
            return defaultValue;
        }
    };

    public ipOnly = (): Promise<IpOnlyResponse> => {
        return Services.getInstanceOf(FeFavFilterService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.FeFavFilterService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
        }).ipOnly();
    };

    public fetchDeviceFeaturesFromService = async () => {
        const _fetchDeviceFeaturesFromService = this._fetchDeviceFeatures.bind(this);
        return new Promise((resolve, reject) => {
            RetryMechanism.callServiceWithRetry(
                _fetchDeviceFeaturesFromService,
                [],
                <RetryOptions>DEFAULT_RETRY_OPTIONS,
            )
                .then(() => {
                    resolve(true);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    };

    public waitForDeviceFeaturesFromService = () => {
        const fetchDeviceFeaturesData = this._fetchDeviceFeatures.bind(this);
        return new Promise((resolve, reject) => {
            RecoveryMechanism.callServiceWithRecovery(fetchDeviceFeaturesData, [])
                .then(() => {
                    resolve(true);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    };

    public getBrandingUiInfoForKey = (key: string): any => {
        try {
            return Services.getInstanceOf(BrandingUIBundleService, {
                accessToken: DeviceModel.getAccessToken(),
                basePath:
                    ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.BrandingUIBundleService],
                bodyId: DeviceModel.getTiVoSettings().getBodyId(),
                slsDomain: DeviceModel.getTiVoSettings().getSlsDomain(),
                whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
            }).getBrandingUiInfoForKey(key);
        } catch (error) {
            return Branding.getFallbackBrandingUiInfoForKey(key);
        }
    };

    // TODO: This method has to move to brandingUiUtility after removing the cyclic dependency
    // from this file
    public getAppUiImageByKey = (key: string): any => {
        const images = this.getBrandingUiInfoForKey(BrandingUiInfoKeys.APP_IMAGES) || [];
        // eslint-disable-next-line no-restricted-syntax
        for (const imageObj of images) {
            if (imageObj.imageIcon === key) {
                imageObj.imageUrl = imageObj.sourceUrl;
                return imageObj;
            }
        }
        return null;
    };

    public getPartnerBrandingImageUrl(width: number, height: number): string {
        const { height: scaledHeight, width: scaledWidth } =
            DimensionScaler.getInstance().getScaledDimensions(width, height);
        const images = this.getBrandingUiInfoForKey(BrandingUiInfoKeys.IMAGE)?.images ?? [];
        const { imageUrl } = images.reduce(
            (bestImage, currentImage) => {
                const currentAspectRatioDiff = Math.abs(
                    currentImage.width / currentImage.height - scaledWidth / scaledHeight,
                );
                const scale = Math.max(
                    currentImage.width / scaledWidth,
                    currentImage.height / scaledHeight,
                );
                const scaleDiff = Math.abs(scale - 1.0);

                if (currentAspectRatioDiff < bestImage.aspectRatioDiff) {
                    return {
                        imageUrl: currentImage.imageUrl,
                        aspectRatioDiff: currentAspectRatioDiff,
                        scaleDiff,
                    };
                }
                if (
                    currentAspectRatioDiff === bestImage.aspectRatioDiff &&
                    scaleDiff < bestImage.scaleDiff
                ) {
                    return {
                        imageUrl: currentImage.imageUrl,
                        aspectRatioDiff: currentAspectRatioDiff,
                        scaleDiff,
                    };
                }

                return bestImage;
            },
            { imageUrl: '', aspectRatioDiff: Infinity, scaleDiff: Infinity },
        );

        return imageUrl || '';
    }

    // TODO: uncomment in phase 2
    // getPDSnapshots = (): Promise<{ [key: string]: string }> => {
    //     return Services.getInstanceOf(PDSnapshotService, {
    //         accessToken: DeviceModel.getAccessToken(),
    //         basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.PDChangeService],
    //         bodyId: DeviceModel.getTiVoSettings().getBodyId(),
    //     }).getPDSnapshots();
    // };

    public getMyWanIpAddress = (): Promise<TIpAddress> => {
        return Services.getInstanceOf(MyWanIpService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.MyWanIpService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
        }).getMyWanIpAddress();
    };

    public updateChannelUrl = (channelNumber: string, url: string) => {
        return Services.getInstanceOf(
            ChannelsService,
            this.getChannelsServiceParams(),
        ).updateChannelUrl(channelNumber, url);
    };

    public fetchOttOfferActionDetails = (
        actionDetailsParams: TActionDetailsParams,
    ): Promise<TActionDetailsList> => {
        return new Promise((resolve, reject) => {
            const actionDetailsService = Services.getInstanceOf(ActionDetailsService, {
                accessToken: DeviceModel.getAccessToken(),
                basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ActionDetailsService],
                bodyId: DeviceModel.getTiVoSettings().getBodyId(),
                timeZoneName: DeviceModel.getTVProperties().getDefaultTimeZone(),
                whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
            });
            actionDetailsService
                .fetchOfferActionDetails(actionDetailsParams)
                .then((data) => {
                    this._validateActionDetailsServiceResponse(data);
                    resolve(data);
                })
                .catch((error: any) => {
                    let actionDetailsError = error;
                    if (
                        !(
                            error instanceof EmptyResponseError ||
                            error instanceof PartialResponseError
                        )
                    ) {
                        const actionDetailsServiceError = error?.response?.data;
                        actionDetailsError = new OfferActionDetailsError(
                            actionDetailsServiceError?.code,
                            actionDetailsServiceError?.message,
                            this.formatSocuErrorDescription(
                                Locale.getString(LocaleKeys.SOCU_ERROR_OVERLAY_DESCRIPTION),
                            ),
                        );
                    }
                    reject(actionDetailsError);
                });
        });
    };

    public setSocuLastViewedPosition = (
        socuLVPParams: TVodSocuLastViewedPositionParams,
    ): Promise<void> => {
        return Services.getInstanceOf(VodSocuLastViewedPositionService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath:
                ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.VodSocuLastViewedPositionService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).setSocuLastViewedPosition(socuLVPParams);
    };

    public trackNetworkChange = async () => {
        let ips: TIpAddress | null = null;
        try {
            ips = await this.getMyWanIpAddress();
        } catch (error) {
            Log.e(ServicesClientImpl.TAG, 'trackNetworkChange', error);
        }

        let isNetWorkChanged = !ips;
        if (ips) {
            isNetWorkChanged = isNetWorkChanged || ips.newWanIp !== ips.oldWanIp;
            isNetWorkChanged = isNetWorkChanged || ips.newWanIp === '';
            isNetWorkChanged = isNetWorkChanged || ips.oldWanIp === '';
        }

        if (isNetWorkChanged) {
            this.fetchAndStoreInHomeLocation().catch((error) => {
                Log.e(
                    ServicesClientImpl.TAG,
                    'trackNetworkChange::fetchAndStoreInHomeLocation',
                    error,
                );
            });
        }
    };

    public recoverInHomeLocationInfo = (): Promise<boolean> => {
        const fetchAndStoreInHomeLocation = this.fetchAndStoreInHomeLocation.bind(this);
        return new Promise((resolve) => {
            RecoveryMechanism.callServiceWithRecovery(fetchAndStoreInHomeLocation, []).then(() => {
                resolve(true);
            });
        });
    };

    /**
     * MVP 3.0
     * please add only MVP 3.0 related funcation below this in sorted order
     */

    /**
     * Below are the method related to logging (QoE, Segment, etc.) operations.
     */
    public async getVuLoggerProperties(
        channel: Channel,
        guideCells: GuideCell[],
    ): Promise<TVuLoggerProperties> {
        const itemSelectedIndex: number = guideCells?.findIndex((guideCell: GuideCell) => {
            const oCurrentDateTime = new DateTime();
            const currentEpoch = oCurrentDateTime.getTime();

            const { startTime, duration } = guideCell;
            const startTimeEpoch = startTime.getTime();
            const startEpoch = startTimeEpoch < currentEpoch ? currentEpoch : startTimeEpoch;

            const endEpoch = oCurrentDateTime.addSeconds(duration).getTime();

            return currentEpoch >= startEpoch && currentEpoch <= endEpoch;
        });

        const offerId = guideCells && guideCells[itemSelectedIndex]?.offerId;

        return new Promise<TVuLoggerProperties>((resolve) => {
            this.fetchOfferPreview(offerId, ScreenType.GuideHeader)
                .then((response: TOfferPreview) => {
                    resolve({
                        channel,
                        guideCell: guideCells[itemSelectedIndex],
                        offerPreview: response.offerPreview,
                    });
                })
                .catch(() => {
                    resolve({
                        channel,
                        guideCell: guideCells[itemSelectedIndex],
                        offerPreview: {},
                    });
                });
        });
    }

    /**
     * Below are the method related to cloudcore-recordings
     */

    /**
     * @deprecated Use getRequestModelToCancelRecordingOfferIdSchedule method from NdvrInteractor
     *
     * The method will return the object `TiVoNetworkRequestModel` to `cancel` the in progress
     * recording offerId schedule.
     * @param contentId (string) - The `contentId of the <episode/game/match/event>
     * @param offerId (string) - The `offerId` of the <episode/game/match/event>
     * @returns `TiVoNetworkRequestModel<any>`
     */
    // eslint-disable-next-line class-methods-use-this
    public getRequestModelToCancelRecordingOfferIdSchedule(
        contentId: string,
        offerId: string,
    ): TiVoNetworkRequestModel<any> {
        return Services.getInstanceOf(RecordingsService, {
            accessToken: DeviceModel.getAccessToken(),
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.RecordingsService],
            timeZone: DeviceModel.getTVProperties().getOlsonTimeZone(),
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).getRequestModelToCancelRecordingOfferIdSchedule(contentId, offerId);
    }

    /**
     * @deprecated Use getRequestModelToCreateRecordingOfferIdSchedule method from NdvrInteractor
     *
     * The method will return the object `TiVoNetworkRequestModel` to `create/schedule` the
     * recording offerId schedule.
     * @param contentId (string) - The `contentId of the <episode/game/match/event>
     * @param offerId (string) - The `offerId` of the <episode/game/match/event>
     * @param tRecordingValues (TRecordingValues) - The `tRecordingValues` of the `contentId`
     * and `recordingId`
     * @returns `TiVoNetworkRequestModel<any>`
     */
    // eslint-disable-next-line class-methods-use-this
    public getRequestModelToCreateRecordingOfferIdSchedule(
        contentId: string,
        offerId: string,
        tRecordingValues: TRecordingValues,
    ): TiVoNetworkRequestModel<any> {
        return Services.getInstanceOf(RecordingsService, {
            accessToken: DeviceModel.getAccessToken(),
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.RecordingsService],
            timeZone: DeviceModel.getTVProperties().getOlsonTimeZone(),
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).getRequestModelToCreateRecordingOfferIdSchedule(contentId, offerId, tRecordingValues);
    }

    /**
     * @deprecated Use getRequestModelToDeleteRecordingSchedule method from NdvrInteractor
     *
     * The method will return the object `TiVoNetworkRequestModel` to `delete` the existing or in
     * progress recording schedule.
     * @param recordingId (string) - The `recordingId` of the <episode/game/match/event>
     */
    // eslint-disable-next-line class-methods-use-this
    public getRequestModelToDeleteRecordingSchedule(
        recordingId: string,
    ): TiVoNetworkRequestModel<any> {
        return Services.getInstanceOf(RecordingsService, {
            accessToken: DeviceModel.getAccessToken(),
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.RecordingsService],
            timeZone: DeviceModel.getTVProperties().getOlsonTimeZone(),
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).getRequestModelToDeleteRecordingSchedule(recordingId);
    }

    /**
     * @deprecated Use getRecordingOfferIdOptionsRequestModel method from NdvrInteractor
     *
     * The method will return the object `TiVoNetworkRequestModel` to get `TRecordingOptions`.
     * @param contentId (string) - The `contentId of the <episode/game/match/event>
     * @param offerId (string) - The `offerId` of the <episode/game/match/event>
     * @returns `TiVoNetworkRequestModel<TRecordingOptions>`
     */
    // eslint-disable-next-line class-methods-use-this
    public getRecordingOfferIdOptionsRequestModel(
        contentId: string,
        offerId: string,
    ): TiVoNetworkRequestModel<TRecordingOptions> {
        return Services.getInstanceOf(RecordingsService, {
            accessToken: DeviceModel.getAccessToken(),
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.RecordingsService],
            timeZone: DeviceModel.getTVProperties().getOlsonTimeZone(),
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).getRequestModelForOfferIdRecordingOptions(contentId, offerId);
    }

    /**
     * @deprecated Use getRecordingOfferIdScheduleRequestModel method from NdvrInteractor
     *
     * The method will return the object `TiVoNetworkRequestModel` to get existing or default
     * `TRecordingValuesWithDisplayOptions`.
     * @param contentId (string) - The `contentId of the <episode/game/match/event>
     * @param offerId (string) - The `offerId` of the <episode/game/match/event>
     * @returns `TiVoNetworkRequestModel<TRecordingValuesWithDisplayOptions>`
     */
    // eslint-disable-next-line class-methods-use-this
    public getRecordingOfferIdScheduleRequestModel(
        contentId: string,
        offerId: string,
    ): TiVoNetworkRequestModel<TRecordingValuesWithDisplayOptions> {
        return Services.getInstanceOf(RecordingsService, {
            accessToken: DeviceModel.getAccessToken(),
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.RecordingsService],
            timeZone: DeviceModel.getTVProperties().getOlsonTimeZone(),
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).getRequestModelForRecordingValuesWithDisplayOptions(contentId, offerId);
    }

    /**
     * @deprecated Use getRecordingOptionsRequestModel method from NdvrInteractor
     *
     * The method will return the object `TiVoNetworkRequestModel` to get the `TRecordingOptions`
     * for existing recording.
     * @param contentId (string) - The `contentId of the <episode/game/match/event>
     * @param recordingId (string) - The `recordingId` of the <episode/game/match/event>
     * @returns `TiVoNetworkRequestModel<TRecordingValues>`
     */
    // eslint-disable-next-line class-methods-use-this
    public getRecordingOptionsRequestModel(
        contentId: string,
        recordingId: string,
    ): TiVoNetworkRequestModel<TRecordingOptions> {
        return Services.getInstanceOf(RecordingsService, {
            accessToken: DeviceModel.getAccessToken(),
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.RecordingsService],
            timeZone: DeviceModel.getTVProperties().getOlsonTimeZone(),
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).getRequestModelForRecordingIdRecordingOptions(contentId, recordingId);
    }

    /**
     * @deprecated Use getRecordingScheduleRequestModel method from NdvrInteractor
     *
     * The method will return the object `TiVoNetworkRequestModel` to get the existing
     * `TRecordingValues` for the `recordingId`.
     * @param contentId (string) - The `contentId of the <episode/game/match/event>
     * @param recordingId (string) - The `recordingId` of the <episode/game/match/event>
     * @returns `TiVoNetworkRequestModel<TRecordingValues>`
     */
    // eslint-disable-next-line class-methods-use-this
    public getRecordingScheduleRequestModel(
        contentId: string,
        recordingId: string,
    ): TiVoNetworkRequestModel<TRecordingValues> {
        return Services.getInstanceOf(RecordingsService, {
            accessToken: DeviceModel.getAccessToken(),
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.RecordingsService],
            timeZone: DeviceModel.getTVProperties().getOlsonTimeZone(),
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).getRequestModelForRecordingValues(contentId, recordingId);
    }

    /**
     * @deprecated Use getRequestModelToRecordingRecovery method from NdvrInteractor
     *
     * The method will return the object `TiVoNetworkRequestModel` to `recover` a recently
     * deleted recording.
     * @param recordingId (string) - The `recordingId` of the <episode/game/match/event>
     * @returns `TiVoNetworkRequestModel<any>`
     */
    // eslint-disable-next-line class-methods-use-this
    public getRequestModelToRecordingRecovery(recordingId: string): TiVoNetworkRequestModel<any> {
        return Services.getInstanceOf(RecordingsService, {
            accessToken: DeviceModel.getAccessToken(),
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.RecordingsService],
            timeZone: DeviceModel.getTVProperties().getOlsonTimeZone(),
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).getRequestModelToRecordingRecovery(recordingId);
    }

    /**
     * @deprecated Use getRequestModelToUpdateRecordingOfferIdSchedule method from NdvrInteractor
     *
     * The method will return the object `TiVoNetworkRequestModel` to `update` the recording values
     * for the `offerId`.
     * @param contentId (string) - The `contentId of the <episode/game/match/event>
     * @param offerId (string) - The `offerId` of the <episode/game/match/event>
     * @param tRecordingValues (TRecordingValues) - The `tRecordingValues` of the `contentId`
     * and `recordingId`.
     * @returns `TiVoNetworkRequestModel<any>`
     */
    // eslint-disable-next-line class-methods-use-this
    public getRequestModelToUpdateRecordingOfferIdSchedule(
        contentId: string,
        offerId: string,
        tRecordingValues: TRecordingValues,
    ): TiVoNetworkRequestModel<any> {
        return Services.getInstanceOf(RecordingsService, {
            accessToken: DeviceModel.getAccessToken(),
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.RecordingsService],
            timeZone: DeviceModel.getTVProperties().getOlsonTimeZone(),
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).getRequestModelToUpdateRecordingOfferIdSchedule(contentId, offerId, tRecordingValues);
    }

    /**
     * @deprecated Use getRequestModelToUpdateRecordingSchedule method from NdvrInteractor
     *
     * The method will return the object `TiVoNetworkRequestModel` to `update` the recording values
     * for the existing `recordingId`.
     * @param contentId (string) - The `contentId of the <episode/game/match/event>
     * @param recordingId (string) - The `recordingId` of the <episode/game/match/event>
     * @param tRecordingValues (TRecordingValues) - The `tRecordingValues` of the `contentId`
     * and `recordingId`.
     * @returns `TiVoNetworkRequestModel<any>`
     */
    // eslint-disable-next-line class-methods-use-this
    public getRequestModelToUpdateRecordingSchedule(
        contentId: string,
        recordingId: string,
        tRecordingValues: TRecordingValues,
    ): TiVoNetworkRequestModel<any> {
        return Services.getInstanceOf(RecordingsService, {
            accessToken: DeviceModel.getAccessToken(),
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.RecordingsService],
            timeZone: DeviceModel.getTVProperties().getOlsonTimeZone(),
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).getRequestModelToUpdateRecordingSchedule(contentId, recordingId, tRecordingValues);
    }

    /**
     * Below are the method related to cloudcore-onepass
     */

    /**
     * @deprecated Use getOnePassList method from NdvrInteractor
     */
    // eslint-disable-next-line class-methods-use-this
    public async getOnePassList(onePassListParams: TOnePassListParams): Promise<TOnePassList> {
        return Services.getInstanceOf(OnePassService, {
            accessToken: DeviceModel.getAccessToken(),
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.OnePassService],
            timeZone: DeviceModel.getTVProperties().getDefaultTimeZone(),
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).getOnePassList(onePassListParams);
    }

    /**
     * @deprecated Use getOnePassOptions method from NdvrInteractor
     */
    // eslint-disable-next-line class-methods-use-this
    public async getOnePassOptions(
        unifiedCollectionId: string,
        networkCallId?: number,
        channelId?: string,
        contentId?: string,
    ): Promise<TOnePassOptions> {
        return Services.getInstanceOf(OnePassService, {
            accessToken: DeviceModel.getAccessToken(),
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.OnePassService],
            timeZone: DeviceModel.getTVProperties().getDefaultTimeZone(),
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).getOnePassOptions(unifiedCollectionId, networkCallId, channelId, contentId);
    }

    /**
     * @deprecated Use createOnePass method from NdvrInteractor
     */
    // eslint-disable-next-line class-methods-use-this
    public createOnePass(
        unifiedCollectionId: string,
        onePassValues: OnePassValues,
        networkCallId?: number | undefined,
    ): Promise<void> {
        return Services.getInstanceOf(OnePassService, {
            accessToken: DeviceModel.getAccessToken(),
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.OnePassService],
            timeZone: DeviceModel.getTVProperties().getDefaultTimeZone(),
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).createOnePass(unifiedCollectionId, onePassValues, networkCallId);
    }

    /**
     * @deprecated Use deleteOnePass method from NdvrInteractor
     */
    // eslint-disable-next-line class-methods-use-this
    public deleteOnePass(
        unifiedCollectionId: string,
        networkCallId?: number | undefined,
    ): Promise<void> {
        return Services.getInstanceOf(OnePassService, {
            accessToken: DeviceModel.getAccessToken(),
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.OnePassService],
            timeZone: DeviceModel.getTVProperties().getDefaultTimeZone(),
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).deleteOnePass(unifiedCollectionId, networkCallId);
    }

    // eslint-disable-next-line class-methods-use-this
    public getOfferActionsV2(actionsParams: TActionsParams): Promise<any> {
        // TODO: Need to fix this any with a type when structure is confirmed
        return new Promise((resolve, reject) => {
            const offerActionsService = Services.getInstanceOf(ActionsService, {
                accessToken: DeviceModel.getAccessToken(),
                basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ActionsService],
                bodyId: DeviceModel.getTiVoSettings().getBodyId(),
                timeZoneName: DeviceModel.getTVProperties().getDefaultTimeZone(),
                whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
            });
            offerActionsService
                .getOfferActionsV2(actionsParams)
                .then((data) => {
                    this._validateOfferActionsServiceV2Response(data);
                    resolve(data);
                })
                .catch((error: any) => {
                    let offerActionError = error;
                    if (!(error instanceof EmptyResponseError)) {
                        const offerActionServiceError = error?.response?.data;
                        offerActionError = new OfferActionError(
                            offerActionServiceError?.message,
                            offerActionServiceError?.code,
                            this.formatSocuErrorDescription(
                                Locale.getString(LocaleKeys.SOCU_ERROR_OVERLAY_DESCRIPTION),
                            ),
                        );
                    }
                    reject(offerActionError);
                });
        });
    }

    // eslint-disable-next-line class-methods-use-this
    public getRecordingActionsV2(actionsParams: TActionsRecordingParams): Promise<TActionList> {
        return Services.getInstanceOf(ActionsService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ActionsService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            timeZoneName: DeviceModel.getTVProperties().getDefaultTimeZone(),
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).getRecordingActionsV2(actionsParams);
    }

    // eslint-disable-next-line class-methods-use-this
    public getVodOfferActionsV2(actionsParams: TActionsVodOfferParams): Promise<TActionList> {
        return Services.getInstanceOf(ActionsService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ActionsService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            timeZoneName: DeviceModel.getTVProperties().getDefaultTimeZone(),
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).getVodOfferActionsV2(actionsParams);
    }

    /**
     * @deprecated Use getValuesWithDisplayOptions method from NdvrInteractor
     */
    // eslint-disable-next-line class-methods-use-this
    public getValuesWithDisplayOptions(
        unifiedCollectionId: string,
        networkCallId?: number | undefined,
        channelId?: string,
        contentId?: string,
    ): Promise<TOnePassValuesWithDisplayOptions> {
        return Services.getInstanceOf(OnePassService, {
            accessToken: DeviceModel.getAccessToken(),
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.OnePassService],
            timeZone: DeviceModel.getTVProperties().getDefaultTimeZone(),
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).getValuesWithDisplayOptions(unifiedCollectionId, networkCallId, channelId, contentId);
    }

    // eslint-disable-next-line class-methods-use-this
    public fetchOfferPreviewV2(
        offerId: string,
        screenType: ScreenType,
        networkCallId?: number,
    ): Promise<TOfferPreview> {
        return Services.getInstanceOf(PreviewsService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.PreviewsService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            imageHostBaseUrl: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ImageHost],
            timeZoneName: DeviceModel.getTVProperties().getDefaultTimeZone(),
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).fetchOfferPreviewV2(offerId, screenType, networkCallId);
    }

    public getContentPreviewV2ForContentId = (contentId: string): Promise<TContentPreview> => {
        return Services.getInstanceOf(PreviewsService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.PreviewsService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            imageHostBaseUrl: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ImageHost],
            timeZoneName: DeviceModel.getTVProperties().getDefaultTimeZone(),
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).getContentPreviewV2ForContentId(contentId);
    };

    public fetchOttContentActionDetails = async (
        actionDetailsParams: TActionDetailsParamsContentId,
    ): Promise<TActionDetailsList> => {
        try {
            const actionDetailsService = Services.getInstanceOf(ActionDetailsService, {
                accessToken: DeviceModel.getAccessToken(),
                basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ActionDetailsService],
                bodyId: DeviceModel.getTiVoSettings().getBodyId(),
                timeZoneName: DeviceModel.getTVProperties().getDefaultTimeZone(),
                whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
            });

            const data = await actionDetailsService.fetchContentActionDetails(actionDetailsParams);
            this._validateActionDetailsServiceResponse(data);
            return data;
        } catch (error: any) {
            let actionDetailsError = error;

            if (!(error instanceof EmptyResponseError || error instanceof PartialResponseError)) {
                const actionDetailsServiceError = error?.response?.data;
                actionDetailsError = new OfferActionDetailsError(
                    actionDetailsServiceError?.code,
                    actionDetailsServiceError?.message,
                    this.formatSocuErrorDescription(
                        Locale.getString(LocaleKeys.SOCU_ERROR_OVERLAY_DESCRIPTION),
                    ),
                );
            }

            throw actionDetailsError;
        }
    };

    /**
     * @deprecated Use updateOnePass method from NdvrInteractor
     */
    // eslint-disable-next-line class-methods-use-this
    public updateOnePass(
        unifiedCollectionId: string,
        onePassValues: OnePassValues,
        networkCallId?: number | undefined,
    ): Promise<void> {
        return Services.getInstanceOf(OnePassService, {
            accessToken: DeviceModel.getAccessToken(),
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.OnePassService],
            timeZone: DeviceModel.getTVProperties().getDefaultTimeZone(),
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).updateOnePass(unifiedCollectionId, onePassValues, networkCallId);
    }

    /**
     * @deprecated Use getDiskSpace method from NdvrInteractor
     */
    public getDiskSpace = (networkCallId?: number): Promise<TStorageInfo> => {
        return Services.getInstanceOf(MyShowsService, {
            accessToken: DeviceModel.getAccessToken(),
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.MyShows],
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).getDiskSpace(networkCallId);
    };

    /**
     * @deprecated Use getRecordingsStatus method from NdvrInteractor
     */
    // eslint-disable-next-line class-methods-use-this
    public getRecordingsStatus() {
        return Services.getInstanceOf(RecordingsStatusService, {
            accessToken: DeviceModel.getAccessToken(),
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.RecordingsStatusService],
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).getRecordingsStatus();
    }

    /**
     * @deprecated Use getMyShowsFilterList method from NdvrInteractor
     */
    // eslint-disable-next-line class-methods-use-this
    public getMyShowsFilterList(): Promise<MyShowsFilterList> {
        return Services.getInstanceOf(MyShowsService, {
            accessToken: DeviceModel.getAccessToken(),
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.MyShows],
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).getMyShowsFilterList();
    }

    /**
     * @deprecated Use getMyShowsUserSettings method from NdvrInteractor
     */
    // eslint-disable-next-line class-methods-use-this
    public getMyShowsUserSettings(): Promise<MyShowsUserSettings> {
        return Services.getInstanceOf(UserSettingsService, {
            accessToken: DeviceModel.getAccessToken(),
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.UserSettingsService],
        }).getMyShowsUserSettings();
    }

    /**
     * @deprecated Use setMyShowsUserSettings method from NdvrInteractor
     */
    // eslint-disable-next-line class-methods-use-this
    public setMyShowsUserSettings(value: MyShowsUserSettingsValue) {
        return Services.getInstanceOf(UserSettingsService, {
            accessToken: DeviceModel.getAccessToken(),
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.UserSettingsService],
        }).setMyShowsUserSettings(value);
    }

    /**
     * @deprecated Use getOnePassAndRecordingsUserSettings method from NdvrInteractor
     */
    // eslint-disable-next-line class-methods-use-this
    public getOnePassAndRecordingsUserSettings(): Promise<OnePassAndRecordingsUserSettings> {
        return Promise.resolve({} as OnePassAndRecordingsUserSettings);
    }

    /**
     * @deprecated Use setOnePassAndRecordingsUserSettings method from NdvrInteractor
     */
    // eslint-disable-next-line class-methods-use-this
    public setOnePassAndRecordingsUserSettings(value: OnePassAndRecordingsUserSettingsValues) {}

    /**
     * @deprecated Use setLastSelectedItem method from NdvrInteractor
     */
    // eslint-disable-next-line class-methods-use-this
    public setLastSelectedItem(myShowsItemId: string, myShowsFilter?: string) {
        return Services.getInstanceOf(MyShowsService, {
            accessToken: DeviceModel.getAccessToken(),
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.MyShows],
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).setLastSelectedItem(myShowsItemId, myShowsFilter);
    }

    public loadPCSettings(): void {
        try {
            Services.getInstanceOf(ParentalControlService, {
                ...this._getPCServiceParams(),
            }).loadPCSettings();
        } catch (error) {
            Log.e(ServicesClientImpl.TAG, 'loadPCSettings', error);
        }
    }

    public checkPCViolations(metadata: TMetadata): boolean {
        return Services.getInstanceOf(ParentalControlService, {
            ...this._getPCServiceParams(),
        }).checkPCViolations(metadata);
    }

    public checkPCViolationsWithRatings(
        internalRatings: TInternalRating[],
        isAdult: boolean,
        contentType: string,
    ): boolean {
        if (this.getPCState() === PCState.Off) return false;
        return Services.getInstanceOf(ParentalControlService, {
            ...this._getPCServiceParams(),
        }).checkPCViolationsWithRatings(internalRatings, isAdult, contentType);
    }

    public getPCSetting(key: string): string {
        return Services.getInstanceOf(ParentalControlService, {
            ...this._getPCServiceParams(),
        }).getPCSetting(key);
    }

    public updatePCSetting(key: string, value: string): void {
        return Services.getInstanceOf(ParentalControlService, {
            ...this._getPCServiceParams(),
        }).updatePCSetting(key, value);
    }

    public verifyPCPin(pin: string): boolean {
        return Services.getInstanceOf(ParentalControlService, {
            ...this._getPCServiceParams(),
        }).verifyPCPin(pin);
    }

    public isPCHideAdultContentEnabled(): boolean {
        return Services.getInstanceOf(ParentalControlService, {
            ...this._getPCServiceParams(),
        }).isPCHideAdultContentEnabled();
    }

    public getPCHighestAllowedRatings(): Array<{ [key: string]: string }> {
        return Services.getInstanceOf(ParentalControlService, {
            ...this._getPCServiceParams(),
        }).getPCHighestAllowedRatings();
    }

    public getPCUnratedContentAllowed(): Array<{ [key: string]: string }> {
        return Services.getInstanceOf(ParentalControlService, {
            ...this._getPCServiceParams(),
        }).getPCUnratedContentAllowed();
    }

    public getPCRatingLevel(ratingTypeId: string, ratingValueId: string): number | undefined {
        return Services.getInstanceOf(ParentalControlService, {
            ...this._getPCServiceParams(),
        }).getPCRatingLevel(ratingTypeId, ratingValueId);
    }

    public getMatchedRating(internalRatings: TInternalRating[]): TInternalRating[] {
        return Services.getInstanceOf(ParentalControlService, {
            ...this._getPCServiceParams(),
        }).getMatchedRating(internalRatings);
    }

    public checkRatingViolations(internalRatings: Required<TInternalRating>): boolean {
        return Services.getInstanceOf(ParentalControlService, {
            ...this._getPCServiceParams(),
        }).checkRatingViolations(internalRatings);
    }

    public getPCMovieRatingTypeId(): string {
        return Services.getInstanceOf(ParentalControlService, {
            ...this._getPCServiceParams(),
        }).getPCMovieRatingTypeId();
    }

    public getPCTVRatingTypeId(): string {
        return Services.getInstanceOf(ParentalControlService, {
            ...this._getPCServiceParams(),
        }).getPCTVRatingTypeId();
    }

    public getPCRatingsData(): TPCRatingsData[] {
        return Services.getInstanceOf(ParentalControlService, {
            ...this._getPCServiceParams(),
        }).getPCRatingsData();
    }

    public getPCOptionList(): Array<{
        [key: string]: TPCRating[];
    }> {
        return Services.getInstanceOf(ParentalControlService, {
            ...this._getPCServiceParams(),
        }).getPCOptionList();
    }

    public getPCRatingOptions(): Array<{
        [key: string]: TPCRating[];
    }> {
        return Services.getInstanceOf(ParentalControlService, {
            ...this._getPCServiceParams(),
        }).getPCRatingOptions();
    }

    public createOptionsList(): void {
        return Services.getInstanceOf(ParentalControlService, {
            ...this._getPCServiceParams(),
        }).createOptionsList();
    }

    // eslint-disable-next-line class-methods-use-this
    public getPCAllowedRatings(position: number, ratingName: string): string[] {
        const allowAllExceptAdultText = `${Locale.getString(LocaleKeys.SETTINGS_PC_ALLOW_ALL_EXCEPT_TEXT)} ${Locale.getString(LocaleKeys.SETTINGS_PC_ADULT_TEXT)}`;
        const allowAllText = Locale.getString(LocaleKeys.SETTINGS_PC_ALLOW_ALL_TEXT);
        const blockAllText = Locale.getString(LocaleKeys.SETTINGS_PC_BLOCK_ALL_TEXT);
        const optionList = this.getPCOptionList();
        const ratingsData = this.getPCRatingsData();
        const ratings = [...Object.values(optionList[position])[0]];
        const isHideAdultContentEnabled = this.isPCHideAdultContentEnabled();
        const { adultLevel } = Object.values(ratingsData[position])[0];

        if (isHideAdultContentEnabled)
            ratings.unshift({
                Adult: { level: adultLevel, description: '', ratingValueName: 'Adult' },
            });

        const foundRating = ratings.find(
            (rating) => Object.values(rating)[0].ratingValueName === ratingName,
        );

        const ratingLevel = foundRating ? Object.values(foundRating)[0].level : 0;

        let allowedRatings: {
            [key: string]: { level: number; description: string; ratingValueName: string };
        }[] = [];

        if (ratingName === allowAllExceptAdultText) {
            const shiftedRating = ratings.shift();
            if (shiftedRating) {
                allowedRatings = ratings;
            }
        } else if (ratingName === allowAllText) {
            allowedRatings = ratings;
        } else if (ratingName === blockAllText) {
            allowedRatings = [];
        } else {
            allowedRatings = ratings.filter((rating) => {
                return Object.values(rating)[0].level <= ratingLevel;
            });
        }

        return allowedRatings.map((rating) => Object.values(rating)[0].ratingValueName);
    }

    // eslint-disable-next-line class-methods-use-this
    public getPCBlockedRatings(position: number, ratingName: string): string[] {
        const allowAllExceptAdultText = `${Locale.getString(LocaleKeys.SETTINGS_PC_ALLOW_ALL_EXCEPT_TEXT)} ${Locale.getString(LocaleKeys.SETTINGS_PC_ADULT_TEXT)}`;
        const allowAllText = Locale.getString(LocaleKeys.SETTINGS_PC_ALLOW_ALL_TEXT);
        const blockAllText = Locale.getString(LocaleKeys.SETTINGS_PC_BLOCK_ALL_TEXT);
        const optionList = this.getPCOptionList();
        const ratingsData = this.getPCRatingsData();
        const ratings = [...Object.values(optionList[position])[0]];
        const isHideAdultContentEnabled = this.isPCHideAdultContentEnabled();
        const { adultLevel } = Object.values(ratingsData[position])[0];

        if (isHideAdultContentEnabled)
            ratings.unshift({
                Adult: { level: adultLevel, description: '', ratingValueName: 'Adult' },
            });

        const foundRating = ratings.find(
            (rating) => Object.values(rating)[0].ratingValueName === ratingName,
        );

        const ratingLevel = foundRating ? Object.values(foundRating)[0].level : 0;
        let blockedRatings: {
            [key: string]: { level: number; description: string; ratingValueName: string };
        }[] = [];

        if (ratingName === allowAllExceptAdultText) {
            const shiftedRating = ratings.shift();
            if (shiftedRating) {
                blockedRatings = [
                    {
                        Adult: { level: adultLevel, description: '', ratingValueName: 'Adult' },
                    },
                ];
            }
        } else if (ratingName === allowAllText) {
            blockedRatings = [];
        } else if (ratingName === blockAllText) {
            blockedRatings = ratings;
        } else {
            blockedRatings = ratings.filter((rating) => {
                return Object.values(rating)[0].level > ratingLevel;
            });
        }

        return blockedRatings.map((rating) => Object.values(rating)[0].ratingValueName);
    }

    /**
     * Returns rating name for which restriction has been applied.
     * If it doesn't return anything it means rating is unknown.
     * Must use this with `checkPCViolations` method.
     */
    public getRestrictedRatingName(data: TMetadata | TInternalRating[]): string | undefined {
        let internalRatings: TInternalRating[];
        if (Array.isArray(data)) internalRatings = data;
        else {
            const { airingOffers, offerPreview } = data;
            internalRatings = (airingOffers?.[0]?.parentalControls?.rating ??
                offerPreview?.contentPreview?.internalRatingList ??
                []) as TInternalRating[];
        }

        const matchingRatings = this.getMatchedRating(internalRatings);

        const restrictedRating = matchingRatings.find((rating) =>
            this.checkRatingViolations(rating as Required<TInternalRating>),
        );

        return restrictedRating
            ? this.getInternalRatingName(`${restrictedRating.typeId}${restrictedRating.valueId}`)
            : undefined;
    }

    public getPCRatingDescription(ratingName: string): string {
        const internalRatingsData = JSON.parse(this.getPCSetting(PCSettings.RATINGS_INSTRUCTIONS));
        const mInternalRatingsData: TExtendedRatingLimits = Object(internalRatingsData);

        const formattedRatings = mInternalRatingsData.ratingValue.map((rating) => {
            return { [rating.name]: String(rating.description) };
        });

        const index = formattedRatings.findIndex((rating) => {
            return Object.keys(rating)[0] === ratingName;
        });

        return formattedRatings[index][ratingName];
    }

    // eslint-disable-next-line class-methods-use-this
    public getPCRatingsOptions(): Array<{
        text: string;
        options: string[];
        descriptions: string[];
        ratingTypeId: string;
    }> {
        const ratingsOptions = this.getPCRatingOptions();
        const ratingsData = this.getPCRatingsData();
        const updatedRatingsOptions = ratingsOptions.map((ratingOption) => {
            const updatedOptions = Object.values(ratingOption)[0].map(
                (option) => Object.values(option)[0].ratingValueName,
            );
            const ratingDescription = Object.values(ratingOption)[0].map(
                (option) => Object.values(option)[0].description,
            );
            const ratingTypeName = ratingsData.filter((data) => {
                return Object.keys(data)[0] === Object.keys(ratingOption)[0];
            })[0][Object.keys(ratingOption)[0]].name;
            const text = Locale.getString(
                LocaleKeys.SETTINGS_PC_HIGHEST_ALLOWED_RATING_NAME_TEXT,
            ).replace('RATING_NAME', ratingTypeName);
            return {
                text,
                options: updatedOptions,
                descriptions: ratingDescription,
                ratingTypeId: Object.keys(ratingOption)[0],
            };
        });
        return updatedRatingsOptions;
    }

    // eslint-disable-next-line class-methods-use-this
    public getPCUnratedOptions(): Array<{
        text: string;
        options: string[];
        descriptions: string[];
        note: string;
        ratingTypeId: string;
    }> {
        const ratingsOptions = this.getPCRatingOptions();
        const ratingsData = this.getPCRatingsData();
        const updatedRatingsOptions = ratingsOptions.map((ratingOption) => {
            const updatedOptions = [
                Locale.getString(LocaleKeys.SETTINGS_PC_BLOCK_ALL_TEXT),
                Locale.getString(LocaleKeys.SETTINGS_PC_ALLOW_ALL_TEXT),
            ];
            const ratingTypeName = ratingsData.filter((data) => {
                return Object.keys(data)[0] === Object.keys(ratingOption)[0];
            })[0][Object.keys(ratingOption)[0]].name;
            let text = '';
            const ratingDescription: string[] = [];
            switch (Object.keys(ratingOption)[0]) {
                case this.getPCTVRatingTypeId():
                    ratingDescription.push(
                        `${Locale.getString(LocaleKeys.SETTINGS_PC_BLOCK_ALL_TEXT)}: ${Locale.getString(LocaleKeys.SETTINGS_PC_BLOCK_ALL_TV_SHOWS_PREVIEW_TEXT)}`,
                    );
                    ratingDescription.push(
                        `${Locale.getString(LocaleKeys.SETTINGS_PC_ALLOW_ALL_TEXT)}: ${Locale.getString(LocaleKeys.SETTINGS_PC_ALLOW_ALL_TV_SHOWS_PREVIEW_TEXT)}`,
                    );
                    text = Locale.getString(LocaleKeys.SETTINGS_PC_UNRATED_TV_SHOWS_TEXT);
                    break;
                case this.getPCMovieRatingTypeId():
                    ratingDescription.push(
                        `${Locale.getString(LocaleKeys.SETTINGS_PC_BLOCK_ALL_TEXT)}: ${Locale.getString(LocaleKeys.SETTINGS_PC_BLOCK_ALL_MOVIES_PREVIEW_TEXT)}`,
                    );
                    ratingDescription.push(
                        `${Locale.getString(LocaleKeys.SETTINGS_PC_ALLOW_ALL_TEXT)}: ${Locale.getString(LocaleKeys.SETTINGS_PC_ALLOW_ALL_MOVIES_PREVIEW_TEXT)}`,
                    );
                    text = Locale.getString(LocaleKeys.SETTINGS_PC_UNRATED_MOVIES_TEXT);
                    break;
                default:
                    ratingDescription.push(
                        `${Locale.getString(LocaleKeys.SETTINGS_PC_BLOCK_ALL_TEXT)}: ${Locale.getString(LocaleKeys.SETTINGS_PC_UNRATED_BLOCK_ALL_GENERIC_PREVIEW_TEXT)}`,
                    );
                    ratingDescription.push(
                        `${Locale.getString(LocaleKeys.SETTINGS_PC_ALLOW_ALL_TEXT)}: ${Locale.getString(LocaleKeys.SETTINGS_PC_UNRATED_ALLOW_ALL_GENERIC_PREVIEW_TEXT)}`,
                    );
                    text = Locale.getString(
                        LocaleKeys.SETTINGS_PC_UNRATED_RATING_NAME_TEXT,
                    ).replace('RATING_NAME', ratingTypeName);
            }

            return {
                text,
                options: updatedOptions,
                descriptions: ratingDescription,
                note:
                    Object.keys(ratingOption)[0] === this.getPCMovieRatingTypeId()
                        ? Locale.getString(LocaleKeys.SETTINGS_PC_UNRATED_MOVIES_NOTE_TEXT)
                        : '',
                ratingTypeId: Object.keys(ratingOption)[0],
            };
        });
        return updatedRatingsOptions;
    }

    /**
     * Returns the current Parental Control state, default `PCState.Locked` state.
     *
     * @returns `PCState`
     */
    public getPCState(): PCState {
        try {
            return Number(this.getPCSetting(PCSettings.PC_STATUS)) as PCState;
        } catch (error) {
            Log.e(ServicesClientImpl.TAG, 'getPCState', error);
            return PCState.Locked;
        }
    }

    /**
     * Determines whether metadata should be hidden.
     * This should be combined with `isAdult` using an AND operation
     * to decide whether to hide the metadata for any content.
     *
     * @returns {boolean}
     */
    public shouldHideMetadata(): boolean {
        const shouldHideMetadata =
            this.getPCState() !== PCState.Off &&
            this.isPCHideAdultContentEnabled() &&
            !ParentalControlServiceImpl.isPinRecentlyVerified();

        return shouldHideMetadata;
    }

    /**
     * Updates the Parental Control state and  saves it to local storage.
     *
     * @param {PCState} state
     */
    public updatePCState(state: PCState) {
        try {
            const newVerifiedTimestamp =
                state === PCState.Unlocked ? new Date().getTime().toString() : '0';
            this.updatePCSetting(PCSettings.VERIFIED_PIN_TIMESTAMP, newVerifiedTimestamp);
            this.updatePCSetting(PCSettings.PC_STATUS, String(state));
        } catch (error) {
            Log.e(ServicesClientImpl.TAG, 'updatePCState', error);
        }
    }

    /**
     * @deprecated Use getMyShowsItemList method from NdvrInteractor
     */
    // eslint-disable-next-line class-methods-use-this
    public getMyShowsItemList({
        myShowsFilter,
        notCurrentlyAvailable,
        recentlyDeleted,
        streamingMovies,
        count,
        offset,
        orderBy,
        unifiedCollectionId,
    }: TMyShowsItemListParams): Promise<TMyShowsItemList> {
        return Services.getInstanceOf(MyShowsService, {
            accessToken: DeviceModel.getAccessToken(),
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.MyShows],
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
            timeZone: DeviceModel.getTVProperties().getDefaultTimeZone(),
        }).getMyShowsItemList({
            myShowsFilter,
            notCurrentlyAvailable,
            recentlyDeleted,
            streamingMovies,
            count,
            offset,
            orderBy,
            unifiedCollectionId,
        });
    }

    /**
     * @deprecated Use getUpcomingOffersForCollectionId method from NdvrInteractor
     */
    // eslint-disable-next-line class-methods-use-this
    public getUpcomingOffersForCollectionId(
        upcomingOffersParams: TUpcomingOffersParams,
    ): Promise<TUpcomingOffersList> {
        return Services.getInstanceOf(UpcomingOffersService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.UpcomingOffersService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).getUpcomingOffersForCollectionId(upcomingOffersParams);
    }

    /**
     * @deprecated Use getSeasonData method from NdvrInteractor
     */
    // eslint-disable-next-line class-methods-use-this
    public getSeasonData(seasonsParams: TSeasonsParams): Promise<SeasonsData> {
        return Services.getInstanceOf(EpisodeGuideService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.EpisodeGuideService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            timeZoneName: DeviceModel.getTVProperties().getDefaultTimeZone(),
            msoName: DeviceModel.getTiVoSettings().getMsoName(),
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).getSeasonData(seasonsParams);
    }

    /**
     * @deprecated Use clearSeasonsData method from NdvrInteractor
     */
    // eslint-disable-next-line class-methods-use-this
    public clearSeasonsData(): void {
        Services.getInstanceOf(EpisodeGuideService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.EpisodeGuideService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            timeZoneName: DeviceModel.getTVProperties().getDefaultTimeZone(),
            msoName: DeviceModel.getTiVoSettings().getMsoName(),
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).clearSeasonsData();
    }

    private _getParentalSettings() {
        if (!ServicesClientImpl.parentalSettings) {
            ServicesClientImpl.parentalSettings = this.getDeviceFeatureByName(
                PCSettings.PARENTAL_SETTINGS,
                {},
            );
        }
        return ServicesClientImpl.parentalSettings;
    }

    private _getPCServiceParams(): TServicesParams & TPCServiceParams {
        return {
            parentalSettingsData: this._getParentalSettings(),
            defaultPCPin: this.getBrandingUiInfoForKey(PCSettings.DEFAULT_PC_PIN) || '9999',
            ratingsInstructionsData: this.getInternalRatingsData(),
            accessToken: '',
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            basePath: '',
        };
    }

    // eslint-disable-next-line class-methods-use-this
    public fetchRecordingPreview(
        recordingPreviewParams: IRecordingPreviewParams,
    ): Promise<TOfferPreview> {
        return Services.getInstanceOf(PreviewsService, {
            accessToken: DeviceModel.getAccessToken(),
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.PreviewsService],
            timeZoneName: DeviceModel.getTVProperties().getDefaultTimeZone(),
            imageHostBaseUrl: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ImageHost],
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).fetchRecordingPreviewV2(recordingPreviewParams);
    }

    /**
     * @deprecated Use getRequestModelToCreateBookmarkContentIdSchedule method from NdvrInteractor
     *
     * The method will return the object `TiVoNetworkRequestModel` to `create/schedule` the
     * bookmark for the specified contentId.
     * @param contentId (string) - The `contentId of the <episode/game/match/event>
     * @param unifiedCollectionId (string) - The `unifiedCollectionId` of the
     *  <episode/game/match/event/race/movie>
     * @returns `TiVoNetworkRequestModel<any>`
     */
    // eslint-disable-next-line class-methods-use-this
    public getRequestModelToCreateBookmarkContentIdSchedule(
        contentId: string,
        unifiedCollectionId: string,
    ): TiVoNetworkRequestModel<any> {
        return Services.getInstanceOf(BookmarkService, {
            accessToken: DeviceModel.getAccessToken(),
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.BookmarkService],
            timeZone: DeviceModel.getTVProperties().getDefaultTimeZone(),
        }).getRequestModelToCreateBookmarkContentIdSchedule(contentId, unifiedCollectionId);
    }

    /**
     * @deprecated Use getRequestModelToDeleteBookmarkSchedule method from NdvrInteractor
     *
     * The method will return the object `TiVoNetworkRequestModel` to `delete` the existing bookmark
     * for the specified contentId
     * @param contentId (string) - The `contentId` of the <episode/game/match/event/race/movie>
     */
    // eslint-disable-next-line class-methods-use-this
    public getRequestModelToDeleteBookmarkSchedule(
        contentId: string,
    ): TiVoNetworkRequestModel<any> {
        return Services.getInstanceOf(BookmarkService, {
            accessToken: DeviceModel.getAccessToken(),
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.BookmarkService],
            timeZone: DeviceModel.getTVProperties().getDefaultTimeZone(),
        }).getRequestModelToDeleteBookmarkSchedule(contentId);
    }

    /**
     * The method returns Sls Service Endpoints.
     */
    // eslint-disable-next-line class-methods-use-this
    public getSlsServiceEndpoints(): { [key: string]: string } {
        return ServicesClientImpl.servicesBaseUrls;
    }

    // eslint-disable-next-line class-methods-use-this
    public getUnifiedCollectionActions(
        actionsParams: TActionsUnifiedCollectionParams,
    ): Promise<TActionList> {
        return Services.getInstanceOf(ActionsService, {
            accessToken: DeviceModel.getAccessToken(),
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ActionsService],
            timeZoneName: DeviceModel.getTVProperties().getDefaultTimeZone(),
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).getUnifiedCollectionActions(actionsParams);
    }

    // eslint-disable-next-line class-methods-use-this
    public getContentActions(actionsParams: TActionsContentParams): Promise<TActionList> {
        return Services.getInstanceOf(ActionsService, {
            accessToken: DeviceModel.getAccessToken(),
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ActionsService],
            timeZoneName: DeviceModel.getTVProperties().getDefaultTimeZone(),
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).getContentActions(actionsParams);
    }

    public getPlayNextContentActions(
        playNextContentParams: TPlayNextContentActionsParams,
    ): Promise<TActionList> {
        return Services.getInstanceOf(ActionsService, {
            accessToken: DeviceModel.getAccessToken(),
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ActionsService],
            timeZoneName: DeviceModel.getTVProperties().getDefaultTimeZone(),
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).getPlayNextContentActions(playNextContentParams);
    }

    public getPlayNextRecordingActions(
        actionsRecordingParam: TPlayNextRecordingActionsParams,
    ): Promise<TActionList> {
        return Services.getInstanceOf(ActionsService, {
            accessToken: DeviceModel.getAccessToken(),
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ActionsService],
            timeZoneName: DeviceModel.getTVProperties().getDefaultTimeZone(),
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).getPlayNextRecordingActions(actionsRecordingParam);
    }

    public fetchPlayNext(playNextparams: TPlayNextParams): Promise<TPlayNextInfo> {
        return Services.getInstanceOf(EpisodeGuideService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.EpisodeGuideService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            timeZone: DeviceModel.getTVProperties().getDefaultTimeZone(),
            msoName: DeviceModel.getTiVoSettings().getMsoName(),
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).getPlayNext(playNextparams);
    }

    public fetchRecordingPreviewV3(
        recordingPreviewParams: IRecordingPreviewParams,
    ): Promise<TOfferPreview> {
        return Services.getInstanceOf(PreviewsService, {
            accessToken: DeviceModel.getAccessToken(),
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.PreviewsService],
            timeZoneName: DeviceModel.getTVProperties().getDefaultTimeZone(),
            imageHostBaseUrl: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ImageHost],
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).fetchRecordingPreviewV3(recordingPreviewParams);
    }
}
